import React, { useMemo, useState } from 'react';
import { Check, Edit2, Sparkles, X } from 'lucide-react';
import { BrandGuideline } from '../../../types/product';
import { TaskySelect } from '../../tasky';

interface Props {
  guideline: BrandGuideline | undefined;
  onUpdate?: (guideline: BrandGuideline) => void;
  readOnly?: boolean;
  /** 未生成のときにAI生成を走らせる。渡されない場合は手入力の導線のみ出す。 */
  onGenerate?: () => void;
  generating?: boolean;
}

const TONE_OPTIONS = ['professional', 'casual', 'luxury', 'playful', 'sophisticated'];
const STYLE_OPTIONS = ['minimal', 'modern', 'classic', 'bold', 'elegant'];

/** DCMモック共通のカード影 */
const CARD_SHADOW = '0 0 18px 0 rgba(31,23,18,.06)';

/** トーンチップ（モック: bg primary/10・rounded-full） */
const ToneChip: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  if (!children) return null;
  return (
    <span className="rounded-full bg-primary/10 px-3.5 py-1.5 text-xs font-bold text-primary">
      {children}
    </span>
  );
};

/** 使わない言葉の赤チップ（モック: rgba(201,97,98,.08)） */
const NgChip: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  if (!children) return null;
  return (
    <span
      className="rounded-full px-2.5 py-1 text-[11px] font-medium text-destructive"
      style={{ background: 'rgba(201,97,98,0.08)' }}
    >
      {children}
    </span>
  );
};

/** 編集フォームの小ラベル */
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="mb-2 block text-xs font-bold text-gray-500">{children}</span>
);

const editInputClass =
  'w-full rounded-lg border border-border bg-white px-3 py-2 text-[13px] text-dark outline-none focus:border-primary';

const BrandGuidelineSection: React.FC<Props> = ({
  guideline,
  onUpdate,
  readOnly = false,
  onGenerate,
  generating = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<BrandGuideline>(guideline || {});

  const data = editing ? editData : guideline;
  const palette = data?.color_palette || {};
  const canEdit = !readOnly && Boolean(onUpdate);

  const paletteItems = useMemo(
    () => [
      { key: 'primary' as const, label: 'Primary', color: palette.primary },
      { key: 'secondary' as const, label: 'Secondary', color: palette.secondary },
      { key: 'accent' as const, label: 'Accent', color: palette.accent },
      { key: 'background' as const, label: 'Background', color: palette.background },
      { key: 'text' as const, label: 'Text', color: palette.text },
    ],
    [palette],
  );

  const handleSave = () => {
    onUpdate?.(editData);
    setEditing(false);
  };

  if (!data) {
    return (
      <div
        className="flex flex-col items-center justify-center rounded-2xl bg-white px-6 py-14 text-center"
        style={{ boxShadow: CARD_SHADOW }}
      >
        <span className="material-icons-round mb-3 text-[38px] text-[#C4C0B4]">palette</span>
        <div className="text-sm font-semibold text-gray-500">
          ブランドガイドラインはまだ生成されていません
        </div>
        {canEdit && (
          <>
            <div className="mt-1.5 text-xs text-gray-400">
              AIに作らせるか、自分で入力して登録できます。
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              {onGenerate && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={onGenerate}
                  disabled={generating}
                >
                  <Sparkles size={14} />
                  {generating ? '生成中…' : 'AIで生成'}
                </button>
              )}
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:border-[#AEB5DE] hover:bg-[#F5F6FC] hover:text-primary-hover"
                onClick={() => {
                  setEditData({});
                  setEditing(true);
                }}
              >
                <Edit2 size={14} />
                手入力で作成
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  const ngWords = data.regulation?.ng_words || [];
  const requiredNotices = data.regulation?.required_notices || [];

  return (
    <section>
      {/* DCMモック準拠のパネル見出し＋編集アクション */}
      <div className="mt-2 flex items-center gap-3">
        <h2 className="flex items-center gap-2 text-lg font-bold text-dark">
          <span className="material-icons-round text-[21px] text-primary">palette</span>
          ブランドガイドライン
          <span className="help-tip">
            <span className="material-icons-round">help_outline</span>
            <span className="tip-body">
              <b>広告表現のルール。</b>話し方（トーン&マナー・NGワード・必須表記）と
              見た目（カラーパレット・ビジュアルガイド）をここで管理します。
              バナー生成時のコピーとデザインに反映されます。
            </span>
          </span>
        </h2>
        <div className="flex-1" />
        {canEdit && (
          editing ? (
            <>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:border-[#AEB5DE] hover:bg-[#F5F6FC] hover:text-primary-hover"
                onClick={() => {
                  setEditing(false);
                  setEditData(guideline || {});
                }}
              >
                <X size={14} />
                キャンセル
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-hover"
                onClick={handleSave}
              >
                <Check size={14} />
                保存
              </button>
            </>
          ) : (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:border-[#AEB5DE] hover:bg-[#F5F6FC] hover:text-primary-hover"
              onClick={() => {
                setEditData(guideline || {});
                setEditing(true);
              }}
            >
              <Edit2 size={14} />
              編集
            </button>
          )
        )}
      </div>

      {/* 2カラム: 話し方 / 見た目（モック data-panel="brand" 準拠） */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* ===== 話し方 ===== */}
        <div className="rounded-2xl bg-white p-5" style={{ boxShadow: CARD_SHADOW }}>
          <div className="mb-3.5 text-[13px] font-bold text-dark">話し方</div>

          {/* トーンチップ / 編集時はセレクト */}
          {editing ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block">
                <FieldLabel>トーン&マナー</FieldLabel>
                <TaskySelect
                  value={editData.tone_and_manner || ''}
                  onChange={(e) => setEditData({ ...editData, tone_and_manner: e.target.value })}
                >
                  <option value="">選択してください</option>
                  {TONE_OPTIONS.map((tone) => (
                    <option key={tone} value={tone}>{tone}</option>
                  ))}
                </TaskySelect>
              </label>
              <label className="block">
                <FieldLabel>デザインスタイル</FieldLabel>
                <TaskySelect
                  value={editData.design_style || ''}
                  onChange={(e) => setEditData({ ...editData, design_style: e.target.value })}
                >
                  <option value="">選択してください</option>
                  {STYLE_OPTIONS.map((style) => (
                    <option key={style} value={style}>{style}</option>
                  ))}
                </TaskySelect>
              </label>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <ToneChip>{data.tone_and_manner || 'トーン未設定'}</ToneChip>
              <ToneChip>{data.design_style || 'スタイル未設定'}</ToneChip>
              <ToneChip>{data.regulation?.lp_category}</ToneChip>
            </div>
          )}

          {/* 必須表記（モックのOK例=緑チェック行の器） */}
          <div className="mt-4 border-t border-warm-border pt-3.5">
            <FieldLabel>必須表記</FieldLabel>
            {editing ? (
              <textarea
                className={editInputClass}
                value={(editData.regulation?.required_notices || []).join('\n')}
                onChange={(e) => setEditData({
                  ...editData,
                  regulation: {
                    ...editData.regulation,
                    required_notices: e.target.value.split('\n').map((notice) => notice.trim()).filter(Boolean),
                  },
                })}
                placeholder="1行ごとに1つずつ入力"
                rows={4}
              />
            ) : requiredNotices.length ? (
              <div className="space-y-2">
                {requiredNotices.map((notice) => (
                  <div
                    key={notice}
                    className="flex items-start gap-3 rounded-xl px-3.5 py-2.5"
                    style={{ background: 'rgba(68,194,164,0.08)' }}
                  >
                    <span className="material-icons-round mt-0.5 text-[15px] text-success">check</span>
                    <div className="text-[13px] font-semibold text-gray-700">{notice}</div>
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-xs text-gray-400">未登録</span>
            )}
          </div>

          {/* NGワード（モックの「使わない言葉」=赤チップの器） */}
          <div className="mt-4 border-t border-warm-border pt-3.5">
            <FieldLabel>NGワード</FieldLabel>
            {editing ? (
              <textarea
                className={editInputClass}
                value={(editData.regulation?.ng_words || []).join('\n')}
                onChange={(e) => setEditData({
                  ...editData,
                  regulation: {
                    ...editData.regulation,
                    ng_words: e.target.value.split('\n').map((word) => word.trim()).filter(Boolean),
                  },
                })}
                placeholder="1行ごとに1つずつ入力"
                rows={4}
              />
            ) : ngWords.length ? (
              <div className="flex flex-wrap gap-1.5">
                {ngWords.map((word) => (
                  <NgChip key={word}>{word}</NgChip>
                ))}
              </div>
            ) : (
              <span className="text-xs text-gray-400">未登録</span>
            )}
          </div>
        </div>

        {/* ===== 見た目 ===== */}
        <div className="rounded-2xl bg-white p-5" style={{ boxShadow: CARD_SHADOW }}>
          <div className="mb-3.5 text-[13px] font-bold text-dark">見た目</div>

          {/* カラーパレット（スウォッチ） */}
          {paletteItems.some((item) => item.color) ? (
            <div className="mb-4 flex flex-wrap gap-3">
              {paletteItems.filter((item) => item.color).map((item) => (
                <div key={item.key}>
                  <div
                    className="h-12 w-12 rounded-xl border border-warm-border"
                    style={{ background: item.color }}
                  />
                  <div className="mt-1 text-center text-[10px] text-gray-400">{item.label}</div>
                  <div className="text-center text-[9px] text-gray-300">{item.color}</div>
                </div>
              ))}
            </div>
          ) : (
            !editing && (
              <div className="mb-4 text-xs text-gray-400">カラーパレットは未登録です。</div>
            )
          )}
          {editing && (
            <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {paletteItems.map((item) => (
                <label key={item.key} className="flex items-center gap-2 text-[11px] font-semibold text-gray-500">
                  <input
                    type="color"
                    className="h-8 w-8 cursor-pointer rounded border border-warm-border"
                    value={editData.color_palette?.[item.key] || '#ffffff'}
                    onChange={(e) => setEditData({
                      ...editData,
                      color_palette: { ...editData.color_palette, [item.key]: e.target.value },
                    })}
                  />
                  {item.label}
                </label>
              ))}
            </div>
          )}

          {/* ビジュアルガイド・コンプライアンス（モックの撮影ルール行の器） */}
          <div className="space-y-2 text-[13px] text-gray-600">
            <div className="flex items-start gap-2.5">
              <span className="material-icons-round mt-0.5 text-[16px] text-gray-400">photo</span>
              <div>
                <span className="mr-1.5 text-[11px] font-bold text-gray-400">画像使用</span>
                {data.visual_guide?.image_usage || '未登録'}
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <span className="material-icons-round mt-0.5 text-[16px] text-gray-400">dashboard</span>
              <div>
                <span className="mr-1.5 text-[11px] font-bold text-gray-400">レイアウト傾向</span>
                {data.visual_guide?.layout_tendency || '未登録'}
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <span className="material-icons-round mt-0.5 text-[16px] text-gray-400">gavel</span>
              <div className="min-w-0 flex-1">
                <span className="mr-1.5 text-[11px] font-bold text-gray-400">コンプライアンス備考</span>
                {editing ? (
                  <textarea
                    className={`${editInputClass} mt-1`}
                    value={editData.regulation?.compliance_notes || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      regulation: { ...editData.regulation, compliance_notes: e.target.value },
                    })}
                    placeholder="法規制・社内レギュレーションなど、自由記述"
                    rows={4}
                  />
                ) : (
                  data.regulation?.compliance_notes || '未登録'
                )}
              </div>
            </div>
          </div>

          {/* タイポグラフィ */}
          <div className="mt-4 border-t border-warm-border pt-3.5">
            <FieldLabel>タイポグラフィ</FieldLabel>
            <div className="grid grid-cols-1 gap-x-4 gap-y-2 text-[13px] text-gray-600 sm:grid-cols-2">
              <div>
                <span className="mr-1.5 text-[11px] font-bold text-gray-400">見出し</span>
                {data.typography?.heading_style || '未登録'}
              </div>
              <div>
                <span className="mr-1.5 text-[11px] font-bold text-gray-400">本文</span>
                {data.typography?.body_style || '未登録'}
              </div>
              <div className="sm:col-span-2">
                <span className="mr-1.5 text-[11px] font-bold text-gray-400">推奨フォント</span>
                {data.typography?.font_suggestion || '未登録'}
              </div>
            </div>
          </div>

          {/* ロゴ */}
          <div className="mt-4 border-t border-warm-border pt-3.5">
            <FieldLabel>ロゴ</FieldLabel>
            {data.logo?.url ? (
              <div className="inline-flex items-center justify-center rounded-xl border border-warm-border bg-warm-card p-3">
                <img src={data.logo.url} alt="Brand logo" className="max-h-16 max-w-[180px] object-contain" />
              </div>
            ) : (
              <span className="text-xs text-gray-400">ロゴは未登録です。</span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandGuidelineSection;
